/* eslint-disable class-methods-use-this */
import { SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { Requests } from '@nx-smartmonkey/shared/helpers';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { Organization } from '../../domain/backoffice/organization/Organization';
import { OrganizationDTO } from '../../domain/backoffice/organization/OrganizationDTO';
import { Project } from '../../domain/backoffice/project/Project';
import { ProjectDTO } from '../../domain/backoffice/project/ProjectDTO';
import { User } from '../../domain/backoffice/user/User';
import { UserDTO } from '../../domain/backoffice/user/UserDTO';
import { PlanType } from '../../domain/subscription/Subscription';
import { ContractData } from '../../hooks/backoffice/subscriptions/useUpdateSubscriptionContract';
import { OrganizationMapper } from '../../mappers/backoffice/OrganizationMapper';
import { ProjectMapper } from '../../mappers/backoffice/ProjectMapper';
import { UserMapper } from '../../mappers/backoffice/UserMapper';
import { SessionRepository } from '../session/SessionRepository';
import { BackofficeRepository } from './BackofficeRepository';

export class BackofficeRepositoryApi implements BackofficeRepository {
  constructor(private requests: Requests, private sessionRepository: SessionRepository) {}

  async changePlan({
    user_id,
    extra_vehicles,
    extra_supervisors,
    extra_projects,
    extra_users,
    plan,
    traffic,
    current_period_end,
  }: {
    user_id: string;
    extra_vehicles: number;
    extra_supervisors: number;
    extra_projects: number;
    extra_users: number;
    plan?: PlanType;
    traffic?: boolean;
    current_period_end?: Date;
  }): Promise<void> {
    await this.requests.put({
      uri: `/backoffice/organization/plan`,
      data: {
        user_id,
        extra_vehicles,
        extra_supervisors,
        extra_projects,
        extra_users,
        ...(plan !== undefined ? { plan } : {}),
        ...(traffic !== undefined ? { traffic } : {}),
        ...(current_period_end !== undefined ? { current_period_end } : {}),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async cancelSubscription({ userId }: { userId: string }): Promise<void> {
    await this.requests.delete({
      uri: `/backoffice/organization/subscription?user_id=${userId}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });
  }

  async createTrialSubscription({ organizationId, days }: { organizationId: string; days: number }): Promise<void> {
    await this.requests.post({
      uri: `/backoffice/organization/${organizationId}/subscription/trial`,
      data: {
        days,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });
  }

  async searchOrganizations({
    offset,
    limit,
    sort_by,
    sort_direction,
    predicates = [],
  }: {
    offset?: number;
    limit?: number;
    sort_by?: string;
    sort_direction?: string;
    predicates?: Array<SearchPredicate>;
  }): Promise<PaginatedResult<Organization>> {
    const response = await this.requests.post({
      uri: `/backoffice/organizations/search`,
      data: {
        offset,
        limit,
        predicates: predicates!.map((p) => p.getValue()),
        ...(sort_by !== undefined ? { sort_by } : {}),
        ...(sort_direction !== undefined ? { sort_direction } : {}),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });

    const data = response.data as PaginatedResult<OrganizationDTO>;

    return {
      ...data,
      docs: data.docs.map((organization) => OrganizationMapper.toDomain(organization)),
    };
  }

  async retrieveOrganization({ organizationId }: { organizationId: string }): Promise<Organization> {
    const response = await this.requests.get({
      uri: `/backoffice/organization/${organizationId}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });

    const data = response.data as OrganizationDTO;

    return OrganizationMapper.toDomain(data);
  }

  async searchProjects({
    offset,
    limit,
    sort_by,
    sort_direction,
    predicates = [],
  }: {
    offset?: number;
    limit?: number;
    sort_by?: string;
    sort_direction?: string;
    predicates?: Array<SearchPredicate>;
  }): Promise<PaginatedResult<Project>> {
    const response = await this.requests.post({
      uri: `/backoffice/projects/search`,
      data: {
        offset,
        limit,
        predicates: predicates!.map((p) => p.getValue()),
        ...(sort_by !== undefined ? { sort_by } : {}),
        ...(sort_direction !== undefined ? { sort_direction } : {}),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });

    const data = response.data as PaginatedResult<ProjectDTO>;

    return {
      ...data,
      docs: data.docs.map((project) => ProjectMapper.toDomain(project)),
    };
  }

  async retrieveProject({ projectId }: { projectId: string }): Promise<Project> {
    const response = await this.requests.get({
      uri: `/backoffice/project/${projectId}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });

    const data = response.data as ProjectDTO;

    return ProjectMapper.toDomain(data);
  }

  async searchUsers({
    offset,
    limit,
    sort_by,
    sort_direction,
    predicates = [],
  }: {
    offset?: number;
    limit?: number;
    sort_by?: string;
    sort_direction?: string;
    predicates?: Array<SearchPredicate>;
  }): Promise<PaginatedResult<User>> {
    const response = await this.requests.post({
      uri: `/backoffice/users/search`,
      data: {
        offset,
        limit,
        predicates: predicates!.map((p) => p.getValue()),
        ...(sort_by !== undefined ? { sort_by } : {}),
        ...(sort_direction !== undefined ? { sort_direction } : {}),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });

    const data = response.data as PaginatedResult<UserDTO>;

    return {
      ...data,
      docs: data.docs.map((user) => UserMapper.toDomain(user)),
    };
  }

  async retrieveUser({ userId }: { userId: string }): Promise<User> {
    const response = await this.requests.get({
      uri: `/backoffice/user/${userId}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });

    const data = response.data as UserDTO;

    return UserMapper.toDomain(data);
  }

  async validateUser({ userId }: { userId: string }): Promise<void> {
    await this.requests.get({
      uri: `/backoffice/user/validate`,
      params: {
        user: userId,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async updateUser({
    userId,
    newEmail,
    newPassword,
  }: {
    userId: string;
    newEmail?: string;
    newPassword?: string;
  }): Promise<void> {
    await this.requests.put({
      uri: `/backoffice/user/${userId}`,
      data: {
        ...(newEmail ? { new_email: newEmail } : {}),
        ...(newPassword ? { new_password: newPassword } : {}),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });
  }

  async updateSubscriptionSMS({
    organizationId,
    enabled,
    from,
    increasedCredit,
    autoRechargeAmount,
    autoRechargeAt,
    autoRechargeEnabled,
    autoRechargePrice,
  }: {
    organizationId: string;
    enabled?: boolean;
    from?: string;
    increasedCredit?: number;
    autoRechargeAmount?: number;
    autoRechargeAt?: number;
    autoRechargeEnabled?: boolean;
    autoRechargePrice?: number;
  }): Promise<void> {
    await this.requests.put({
      uri: `/backoffice/organization/${organizationId}/sms`,
      data: {
        enabled,
        from,
        increased_credit: increasedCredit,
        auto_recharge_amount: autoRechargeAmount,
        auto_recharge_at: autoRechargeAt,
        auto_recharge: autoRechargeEnabled,
        auto_recharge_price: autoRechargePrice,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });
  }

  async updateSubscriptionWhatsApp({
    organizationId,
    enabled,
    from,
    subaccountId,
    increasedCredit,
  }: {
    organizationId: string;
    enabled?: boolean;
    from?: string;
    subaccountId?: string;
    increasedCredit?: number;
  }): Promise<void> {
    await this.requests.put({
      uri: `/backoffice/organization/${organizationId}/whatsapp`,
      data: {
        enabled,
        from,
        subaccount_id: subaccountId,
        increased_credit: increasedCredit,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });
  }

  async updateSubscriptionGeocode({
    organizationId,
    increasedCredit,
  }: {
    organizationId: string;
    increasedCredit: number;
  }): Promise<void> {
    await this.requests.put({
      uri: `/backoffice/organization/${organizationId}/geocode_credits`,
      data: {
        increased_credit: increasedCredit,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });
  }

  async updateSubscriptionContract({
    organizationId,
    data,
  }: {
    organizationId: string;
    data: ContractData;
  }): Promise<void> {
    await this.requests.put({
      uri: `/backoffice/organization/${organizationId}/contract`,
      data,
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });
  }

  async updateProjectManagedVehicles({
    projectId,
    managedVehicles,
    prorate,
  }: {
    projectId: string;
    managedVehicles: number | null;
    prorate: boolean;
  }): Promise<void> {
    await this.requests.put({
      uri: `/backoffice/project/${projectId}/managed_vehicles`,
      data: {
        managed_vehicles: managedVehicles,
        prorate,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });
  }

  async updateProjectTraffic({ projectId, traffic }: { projectId: string; traffic: boolean }): Promise<void> {
    await this.requests.put({
      uri: `/backoffice/project/${projectId}/traffic`,
      data: {
        traffic,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });
  }

  async updateWhatsAppTemplate({
    projectId,
    id,
    subaccountId,
    from,
    enabled,
    templateId,
    replacement_values,
  }: {
    projectId: string;
    id: string;
    subaccountId?: string | null;
    from?: string | null;
    enabled?: boolean | undefined;
    templateId?: string | null;
    replacement_values?: Record<string, string>;
  }): Promise<void> {
    await this.requests.put({
      uri: `/backoffice/project/${projectId}/whatsapp_template`,
      data: {
        id,
        subaccount_id: subaccountId,
        from,
        enabled,
        template_id: templateId,
        replacement_values,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
      ignoreAsUser: true,
    });
  }
}
